import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { ExternalLink, Mail, Phone } from "lucide-react";
import React from "react";

interface Manufacturer {
  manufacturerNo: string;
  name: string;
  manufacturerContactPerson: string;
  manufacturerContactEmail: string;
  contactNumber: string;
  country: string;
  website: string;
  countryDisplay: string;
  category: string[];
  description: string;
  status: string;
  totalAssets: string;
  averageConditionScore: string;
}

interface ManufacturerProfileProps {
  manufacturerId: string;
  manufacturer: Manufacturer;
  assets: any;
}

export const ManufacturerProfile: React.FC<ManufacturerProfileProps> = ({
  manufacturerId,
  manufacturer,
  assets,
}) => {
  console.log("assets", assets);
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Manufacturer Profile</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 mt-2">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ValueLabel
              label="Manufacturer ID"
              value={manufacturer?.manufacturerNo}
            />
            <ValueLabel label="Manufacturer Name" value={manufacturer?.name} />
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ValueLabel
              label="Primary Contact Person"
              value={manufacturer?.manufacturerContactPerson}
            />
            <div>
              <p className="text-xs text-muted-foreground tracking-wide">
                Contact Email
              </p>
              <a
                href={`mailto:${manufacturer?.manufacturerContactEmail}`}
                className="text-base text-blue-600 hover:underline flex items-center gap-1"
              >
                {manufacturer?.manufacturerContactEmail}
                <Mail className="h-3 w-3" />
              </a>
            </div>
            <div>
              <p className="text-xs text-muted-foreground tracking-wide">
                Phone Number
              </p>
              <a
                href={`tel:${manufacturer?.contactNumber}`}
                className="text-base text-blue-600 hover:underline flex items-center gap-1"
              >
                {manufacturer?.contactNumber}
                <Phone className="h-3 w-3" />
              </a>
            </div>
            <div>
              <p className="text-xs text-muted-foreground tracking-wide">
                Website
              </p>
              <a
                href={`https://${manufacturer?.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-base text-blue-600 hover:underline flex items-center gap-1"
              >
                {manufacturer?.website}
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>

            <ValueLabel label="Country" value={manufacturer?.country} />

            <div>
              <p className="text-xs text-muted-foreground tracking-wide mb-1">
                Asset Types
              </p>
              <div className="flex flex-wrap gap-2">
                {manufacturer?.category?.map((category, index) => (
                  <Badge key={index} variant="outline" className="text-sm">
                    {category}
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Notes</h3>
              <p className="text-base text-gray-900 leading-relaxed">
                {manufacturer?.description}
              </p>
            </div>
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">
                {manufacturer?.totalAssets}
              </p>
              <p className="text-sm text-gray-500">
                Total Assets from this MFG
              </p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-600">
                {manufacturer?.averageConditionScore}
              </p>
              <p className="text-sm text-gray-500">
                Average Asset Condition Score
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
